/**
 * Admin FAQ API module — CON-CMS-003.
 *
 * Typed functions for admin FAQ category and entry endpoints. Containers and
 * Redux thunks MUST import from this module — never call the shared axios
 * client directly.
 *
 * Endpoints:
 *   GET    /api/v1/admin/cms/faq                       — all categories with entries
 *   POST   /api/v1/admin/cms/faq/categories            — create category
 *   PUT    /api/v1/admin/cms/faq/categories/:id        — update category
 *   DELETE /api/v1/admin/cms/faq/categories/:id        — delete category
 *   POST   /api/v1/admin/cms/faq/entries               — create entry
 *   PUT    /api/v1/admin/cms/faq/entries/:id           — update entry
 *   DELETE /api/v1/admin/cms/faq/entries/:id           — delete entry
 *   PATCH  /api/v1/admin/cms/faq/entries/reorder       — batch reorder entries
 *
 * Backend dependency: PL-CMS tickets. Endpoints may return 404 until those
 * tickets land. The PR description notes this dependency.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  FaqCategoryDTO,
  FaqEntryDTO,
  CreateFaqCategoryPayload,
  UpdateFaqCategoryPayload,
  CreateFaqEntryPayload,
  UpdateFaqEntryPayload,
  ReorderFaqEntriesPayload,
  DeleteFaqCategoryPayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// GET /api/v1/admin/cms/faq
// ---------------------------------------------------------------------------

export async function fetchAdminFaqCategories(): Promise<FaqCategoryDTO[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ categories: FaqCategoryDTO[] }>>(
      '/admin/cms/faq',
    );
    const data = unwrap(res.data, res.status);
    return data.categories;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/faq/categories
// ---------------------------------------------------------------------------

export async function createAdminFaqCategory(
  payload: CreateFaqCategoryPayload,
): Promise<FaqCategoryDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ category: FaqCategoryDTO }>>(
      '/admin/cms/faq/categories',
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.category;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/cms/faq/categories/:id
// ---------------------------------------------------------------------------

export async function updateAdminFaqCategory(
  id: number,
  payload: UpdateFaqCategoryPayload,
): Promise<FaqCategoryDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ category: FaqCategoryDTO }>>(
      `/admin/cms/faq/categories/${id}`,
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.category;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/admin/cms/faq/categories/:id
// ---------------------------------------------------------------------------

export async function deleteAdminFaqCategory(
  id: number,
  payload: DeleteFaqCategoryPayload = {},
): Promise<void> {
  try {
    await apiClient.delete(`/admin/cms/faq/categories/${id}`, {
      data: payload,
    });
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/faq/entries
// ---------------------------------------------------------------------------

export async function createAdminFaqEntry(
  payload: CreateFaqEntryPayload,
): Promise<FaqEntryDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ entry: FaqEntryDTO }>>(
      '/admin/cms/faq/entries',
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.entry;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/cms/faq/entries/:id
// ---------------------------------------------------------------------------

export async function updateAdminFaqEntry(
  id: number,
  payload: UpdateFaqEntryPayload,
): Promise<FaqEntryDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ entry: FaqEntryDTO }>>(
      `/admin/cms/faq/entries/${id}`,
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.entry;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/admin/cms/faq/entries/:id
// ---------------------------------------------------------------------------

export async function deleteAdminFaqEntry(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/cms/faq/entries/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/cms/faq/entries/reorder
// ---------------------------------------------------------------------------

export async function reorderAdminFaqEntries(
  payload: ReorderFaqEntriesPayload,
): Promise<void> {
  try {
    await apiClient.patch('/admin/cms/faq/entries/reorder', payload);
  } catch (err) {
    throwAsApiError(err);
  }
}

export const faqAdminApiClient = {
  fetchCategories: fetchAdminFaqCategories,
  createCategory: createAdminFaqCategory,
  updateCategory: updateAdminFaqCategory,
  deleteCategory: deleteAdminFaqCategory,
  createEntry: createAdminFaqEntry,
  updateEntry: updateAdminFaqEntry,
  deleteEntry: deleteAdminFaqEntry,
  reorderEntries: reorderAdminFaqEntries,
};
